/**
 * Web3Provider - Conditional Wagmi/RainbowKit Provider
 * 
 * CRITICAL FIX (Jan 2026):
 * RainbowKit/WalletConnect initialization causes CSP violations in native SDK
 * environments (Farcaster, Base, World) because:
 * 1. WalletConnect makes API calls to explorer-api.walletconnect.com
 * 2. Native containers block external wallet discovery APIs
 * 
 * SOLUTION:
 * - In native environments: Use minimal wagmi config (no WalletConnect)
 * - In web/desktop: Use full RainbowKit with all connectors
 * 
 * NOTE: We use static imports + conditional rendering because dynamic require()
 *       doesn't work in browser ES modules.
 */

import React, { useMemo } from 'react';
import '@rainbow-me/rainbowkit/styles.css';
import { RainbowKitProvider, darkTheme, useConnectModal } from '@rainbow-me/rainbowkit';
import { WagmiProvider } from 'wagmi';
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { AuthProvider, RainbowKitModalContext } from './AuthContext';
import { config } from '../../wagmi';
import { nativeConfig } from '../../wagmi-native';

const queryClient = new QueryClient();

/**
 * Detect if we're in a native SDK environment
 * Uses early detection from index.html if available
 */
function isNativeSDKEnvironment() {
    if (typeof window === 'undefined') return false;
    
    // Use early detection set in index.html (most reliable)
    const earlyDetection = window.DCW_EARLY_DETECTION;
    const platformOverride = window.DCW_PLATFORM_OVERRIDE;
    
    if (platformOverride === 'farcaster' || 
        platformOverride === 'base-minikit' || 
        platformOverride === 'worldcoin') {
        console.log('[Web3Provider] Native environment detected via PLATFORM_OVERRIDE:', platformOverride);
        return true;
    }
    
    if (earlyDetection?.isFarcaster || earlyDetection?.isBase || earlyDetection?.isWorld) {
        console.log('[Web3Provider] Native environment detected via EARLY_DETECTION:', earlyDetection);
        return true;
    }
    
    // Fallback: Check direct indicators
    const isFarcaster = !!(
        window.farcaster || 
        window.__farcaster_miniapp__ ||
        (window.miniKit?.isFrameContext === true) ||
        /warpcast|farcaster/i.test(navigator.userAgent || '') ||
        /warpcast|farcaster/i.test(document.referrer || '')
    );
    
    const isBase = !isFarcaster && !!(
        window.ethereum?.isBase ||
        window.ethereum?.isBaseMiniKit ||
        window.baseMiniKit ||
        /base\.app/i.test(window.location.href || '') ||
        /base\.app/i.test(document.referrer || '')
    );
    
    const isWorld = !!(
        window.worldcoin ||
        window.WorldApp ||
        /world\s*app|worldcoin/i.test(navigator.userAgent || '')
    );
    
    if (isFarcaster || isBase || isWorld) {
        console.log('[Web3Provider] Native environment detected via fallback:', { isFarcaster, isBase, isWorld });
        return true;
    }
    
    return false;
}

// Determine mode once at module load time (before component renders)
const IS_NATIVE_MODE = typeof window !== 'undefined' && isNativeSDKEnvironment();
console.log('[Web3Provider] Module init - IS_NATIVE_MODE:', IS_NATIVE_MODE);

/**
 * RainbowKit Modal Provider Component
 * Wraps children and provides openConnectModal via context
 */
function RainbowKitModalProvider({ children }) {
    const { openConnectModal } = useConnectModal();
    
    return (
        <RainbowKitModalContext.Provider value={openConnectModal}>
            {children}
        </RainbowKitModalContext.Provider>
    );
}

/**
 * Main Web3Provider - Uses appropriate config based on environment
 * 
 * Both modes use the same structure, but:
 * - Native mode: Uses nativeConfig (no WalletConnect)
 * - Desktop mode: Uses full config with RainbowKit UI
 */
export function Web3Provider({ children }) {
    // Use the pre-computed native mode flag
    const isNative = IS_NATIVE_MODE;
    
    // Select the appropriate wagmi config
    const wagmiConfig = useMemo(() => {
        return isNative ? nativeConfig : config;
    }, [isNative]);
    
    console.log('[Web3Provider] Rendering with mode:', isNative ? 'NATIVE' : 'DESKTOP');
    
    if (isNative) {
        // Native mode: Skip RainbowKit UI, use minimal wagmi
        return (
            <WagmiProvider config={wagmiConfig} reconnectOnMount={false}>
                <QueryClientProvider client={queryClient}>
                    <RainbowKitModalContext.Provider value={null}>
                        <AuthProvider>
                            {children}
                        </AuthProvider>
                    </RainbowKitModalContext.Provider>
                </QueryClientProvider>
            </WagmiProvider>
        );
    }
    
    // Desktop mode: Full RainbowKit with all features
    return (
        <WagmiProvider config={wagmiConfig} reconnectOnMount={false}>
            <QueryClientProvider client={queryClient}>
                <RainbowKitProvider theme={darkTheme({
                    accentColor: '#00ff00', // Hacker green
                    accentColorForeground: 'black',
                    borderRadius: 'small',
                    fontStack: 'system',
                    overlayBlur: 'small',
                })}>
                    <RainbowKitModalProvider>
                        <AuthProvider>
                            {children}
                        </AuthProvider>
                    </RainbowKitModalProvider>
                </RainbowKitProvider>
            </QueryClientProvider>
        </WagmiProvider>
    );
}
